import { FC } from 'react';
import NextLink from 'next/link';
import classnames from 'classnames';
import AnimateSpinner from '../../animate-spinner';

const NEW_TAB_TARGET = '_blank';
const NEW_TAB_REL = 'noopener noreferrer';

type ButtonProps = {
  onClick?: any;
  /**
   * When set, renders an anchor with identical styling instead of a button,
   * so link-shaped actions don't nest interactive elements (invalid HTML).
   * Anchors have no native disabled semantics, so when disabled or loading
   * the anchor renders inert: no href (unfocusable) and aria-disabled.
   */
  href?: string;
  /**
   * Anchor-only. Route `href` through `next/link` so in-app destinations get
   * client-side navigation and prefetching (what `<Link legacyBehavior>`
   * wrapping a Button used to provide). Leave unset for external URLs and
   * for routes served outside the Next router, such as /auth/*.
   */
  isLink?: boolean;
  /** Anchor-only, forwarded to `next/link` when `isLink` is set. */
  prefetch?: boolean;
  /**
   * Anchor-only, and only meaningful alongside `href`. Opening a new tab
   * defaults `rel` to `noopener noreferrer` so a caller cannot leave the opened
   * page with `window.opener` access by forgetting it; pass `rel` to override.
   */
  target?: string;
  rel?: string;
  variant:
    | 'primary'
    | 'secondary'
    | 'secondary-danger'
    | 'tertiary'
    | 'danger'
    | 'greenfriday';
  loading?: boolean;
  disabled?: boolean;
  children?: React.ReactNode;
  dataState?: string;
  /**
   * Test/automation hook. Accepts either `dataCy` or a raw `data-cy`
   * attribute so callers using either spelling render `data-cy` on the
   * underlying button (`dataCy` wins if both are passed).
   */
  dataCy?: string;
  'data-cy'?: string;
  size?: 'standard' | 'small';
  className?: string;
  id?: string;
};

const Button: FC<ButtonProps> = ({
  onClick,
  href,
  isLink,
  prefetch,
  target,
  rel,
  variant,
  loading,
  disabled,
  children,
  dataState,
  dataCy,
  'data-cy': dataCyAttribute,
  size,
  className,
  id
}) => {
  const sharedClassName = `inline-flex outline-0 justify-center text-center rounded-lg transition-colors font-body font-bold text-base ${classnames(
    variant === 'primary'
      ? `border-2 border-transparent py-[7px] px-3 text-white bg-primary-600 hover:bg-primary-800 active:bg-neutral-800`
      : variant === 'secondary'
      ? `py-[7px] px-3 text-primary-600 bg-transparent border-primary border-2 hover:text-primary-800 hover:border-primary-800 active:border-neutral-800 active:text-neutral-800`
      : variant === 'secondary-danger'
      ? `py-[7px] px-3 text-danger-600 bg-transparent border-danger-600 border-2 hover:text-danger-800 hover:border-danger-800 active:border-neutral-800 active:text-neutral-800`
      : variant === 'tertiary'
      ? `my-[9px] mx-3 rounded-none px-0 text-primary-600 bg-transparent border-b-2 border-primary-600 hover:text-primary-800 hover:border-primary-800 active:border-neutral-800 active:text-neutral-800`
      : variant === 'danger'
      ? `py-[7px] px-3 text-white bg-danger-600 hover:bg-danger-800 active:bg-neutral-800`
      : variant === 'greenfriday'
      ? `border-2 border-transparent py-[7px] px-3 text-white bg-greenfriday hover:bg-greenfriday active:bg-greenfriday`
      : ``
  )} ${
    disabled || loading
      ? 'cursor-not-allowed bg-opacity-50 bg-neutral-400 text-opacity-50 border-opacity-50 pointer-events-none hover:bg-opacity-50 hover:bg-neutral-400 active:bg-opacity-50 active:bg-neutral-400 '
      : 'cursor-pointer'
  } ${size === 'small' ? 'text-xs p-1' : ''} ${className ?? ''}`;

  if (href) {
    const isInert = disabled || loading;
    const safeRel =
      rel ?? (target === NEW_TAB_TARGET ? NEW_TAB_REL : undefined);
    if (isLink && !isInert) {
      return (
        <NextLink
          href={href}
          prefetch={prefetch}
          target={target}
          rel={safeRel}
          onClick={onClick}
          className={sharedClassName}
          data-state={dataState}
          data-variant={variant}
          data-cy={dataCy ?? dataCyAttribute}
          id={id}
        >
          {children}
        </NextLink>
      );
    }
    return (
      <a
        href={isInert ? undefined : href}
        target={target}
        rel={safeRel}
        onClick={isInert ? undefined : onClick}
        aria-disabled={isInert || undefined}
        className={sharedClassName}
        data-state={dataState}
        data-variant={variant}
        data-cy={dataCy ?? dataCyAttribute}
        id={id}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      type="submit"
      onClick={onClick}
      disabled={loading || disabled}
      className={sharedClassName}
      data-state={dataState}
      data-variant={variant}
      data-cy={dataCy ?? dataCyAttribute}
      id={id}
    >
      {loading && (
        <AnimateSpinner customClasses={'inline-block mr-2 align-middle'} />
      )}
      {loading ? '' : children}
    </button>
  );
};

export default Button;
